import { Injectable, inject } from '@angular/core';
import { DavesaNavigationItem } from '@davesa/components/navigation';
import { DavesaMockApiService } from '@davesa/lib/mock-api';
import { 
    compactNavigation, 
    defaultNavigation, 
    futuristicNavigation, 
    horizontalNavigation,
    organizationNavigation
} from 'app/mock-api/common/navigation/data';
import { MenuPermissionService } from 'app/core/navigation/menu-permission.service';
import { AuthDavesaV2Service } from 'app/core/auth-davesa/auth-davesa-v2.service';
import { cloneDeep } from 'lodash-es';
import { switchMap, of, catchError, from } from 'rxjs';
import { isOrganizationUser } from 'app/core/utils/user-utils';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private _compact: DavesaNavigationItem[] = compactNavigation;
    private _default: DavesaNavigationItem[] = defaultNavigation;
    private _futuristic: DavesaNavigationItem[] = futuristicNavigation;
    private _horizontal: DavesaNavigationItem[] = horizontalNavigation;
    private _organization: DavesaNavigationItem[] = organizationNavigation;
    private _authDavesaV2Service = inject(AuthDavesaV2Service);

    /**
     * Constructor
     */
    constructor(
        private _davesaMockApiService: DavesaMockApiService,
        private _menuPermissionService: MenuPermissionService
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._davesaMockApiService
            .onGet('api/common/navigation')
            .reply(() => {
                // Check if the current user is an organization user
                const currentUser = this._authDavesaV2Service.loginUser();
                const isOrgUser = isOrganizationUser(currentUser);

                // If organization user, return organization-specific navigation
                if (isOrgUser) {
                    const orgNav = cloneDeep(this._organization);
                    const response = {
                        compact: orgNav,
                        default: orgNav,
                        futuristic: orgNav,
                        horizontal: orgNav,
                    };
                    return of([200, response]);
                }

                // Apply permission filtering to default navigation for regular users
                return this._menuPermissionService.filterNavigationByPermission(cloneDeep(this._default)).pipe(
                    switchMap((filteredDefaultNavigation) => {
                        // Clone the filtered navigation for other layouts
                        const filteredCompactNavigation = cloneDeep(this._compact);
                        const filteredFuturisticNavigation = cloneDeep(this._futuristic);
                        const filteredHorizontalNavigation = cloneDeep(this._horizontal);

                        // Fill compact navigation children using the filtered default navigation
                        filteredCompactNavigation.forEach((compactNavItem) => {
                            filteredDefaultNavigation.forEach((defaultNavItem) => {
                                if (defaultNavItem.id === compactNavItem.id) {
                                    compactNavItem.children = cloneDeep(defaultNavItem.children);
                                }
                            });
                        });

                        // Fill futuristic navigation children using the filtered default navigation
                        filteredFuturisticNavigation.forEach((futuristicNavItem) => {
                            filteredDefaultNavigation.forEach((defaultNavItem) => {
                                if (defaultNavItem.id === futuristicNavItem.id) {
                                    futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                                }
                            });
                        });

                        // Fill horizontal navigation children using the filtered default navigation
                        filteredHorizontalNavigation.forEach((horizontalNavItem) => {
                            filteredDefaultNavigation.forEach((defaultNavItem) => {
                                if (defaultNavItem.id === horizontalNavItem.id) {
                                    horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                                }
                            });
                        });

                        // Prepare the response object
                        const response = {
                            compact: filteredCompactNavigation,
                            default: filteredDefaultNavigation,
                            futuristic: filteredFuturisticNavigation,
                            horizontal: filteredHorizontalNavigation,
                        };

                        // Return the response
                        return of([200, response]);
                    }),
                    catchError(error => {
                        // Fallback: return original navigation without filtering
                        const fallbackResponse = {
                            compact: cloneDeep(this._compact),
                            default: cloneDeep(this._default),
                            futuristic: cloneDeep(this._futuristic),
                            horizontal: cloneDeep(this._horizontal),
                        };
                        
                        return of([200, fallbackResponse]);
                    })
                );
            });
    }
}
